import type { TeamWith } from "@redotech/account-common/base-team";
import { GetUser } from "@redotech/account-common/user";
import type { BulkShipmentOneClickPrintDocumentSetting } from "@redotech/redo-fulfillment-common/bulk-shipment-print";
import { personalizedSlipsAccessible } from "@redotech/redo-fulfillment-common/personalized-slip/personalized-slip-access";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { PrintKind } from "@redotech/redo-fulfillment-group-common/fulfillment-print-status";
import { PrintableDocument } from "@redotech/redo-fulfillment-group-common/print-job";
import { FulfillmentGroupActions } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { LocationId } from "@redotech/redo-inventory-id";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { Provider } from "@redotech/redo-order-common/provider";
import { showNotification, toast } from "@redotech/redo-web/alert";
import { assertNever } from "@redotech/util/type";
import { getCarrierClaimActionEligibility } from "../utils/carrier-claim-action-eligibility";
import type {
  OpenBinSelectionModalParams,
  OpenInventoryAdjustmentWarningModalParams,
} from "./bin-selection-modal";
import {
  getBulkOneClickPrintBatchInput,
  getBulkOneClickPrintDocuments,
  getBulkOneClickPrintPickListPosition,
} from "./bulk-one-click-print";
import { purchaseLabelsWithInventoryChecks } from "./fulfillment-label-purchase-gates";
import type { OpenInsufficientInventoryModalParams } from "./insufficient-inventory-modal";
import {
  PinVerificationConfig,
  PinVerificationType,
} from "./pin-verification-context";
import {
  createDocumentPrintStatusMap,
  getDocumentPrintStatusMapForFulfillmentGroups,
  PrintBatchInput,
} from "./print-batch-modal";
import { getSkippedShipmentsNotificationInstance } from "./skipped-shipments-notification";

// Helper function to get products without bins from fulfillment groups
const getProductsWithoutBins = async (
  client: RedoOutboundLabelsRpcClient,
  fulfillmentGroups: readonly FulfillmentGroupData[],
): Promise<FulfillmentOrderLineItem[]> => {
  const variantIdsToCheck = new Set<string>();
  const lineItemsByVariantId = new Map<string, FulfillmentOrderLineItem>();

  const shopifyFulfillmentGroups = fulfillmentGroups.filter((fg) =>
    fg.providers?.includes(Provider.SHOPIFY),
  );

  for (const group of shopifyFulfillmentGroups) {
    for (const lineItem of group.items) {
      const hasExistingBin = group.lineItemLocations?.some(
        (location) =>
          location.sku === lineItem.sku &&
          location.bins?.some((bin) => bin.bin),
      );

      if (!hasExistingBin && lineItem.variantId) {
        variantIdsToCheck.add(lineItem.variantId);
        lineItemsByVariantId.set(lineItem.variantId, lineItem);
      }
    }
  }

  if (variantIdsToCheck.size === 0) {
    return [];
  }

  try {
    const result = await client.getProductsWithoutBinMetafield({
      identifiers: Array.from(variantIdsToCheck),
    });

    const productsWithoutBins: FulfillmentOrderLineItem[] = [];
    for (const variantId of result.identifiers) {
      const lineItem = lineItemsByVariantId.get(variantId);
      if (lineItem) {
        productsWithoutBins.push(lineItem);
      }
    }

    return productsWithoutBins;
  } catch (error) {
    console.error("Error checking products without bins:", error);
    return [];
  }
};

interface DoOMSActionParams {
  action: FulfillmentGroupActions | null;
  user: GetUser | undefined;
  client: RedoOutboundLabelsRpcClient;
  fulfillmentGroups: readonly FulfillmentGroupData[];
  purchasableGroups: readonly FulfillmentGroupData[];
  team: TeamWith<{ outboundLabels: OutboundLabelSettings }> | undefined;
  resetSelections: () => void;
  reloadBalance: () => void;
  triggerRefresh: () => void;
  printBatch: (input: PrintBatchInput) => void;
  bulkOneClickPrintDocuments?: readonly BulkShipmentOneClickPrintDocumentSetting[];
  onGroupsFulfillment: (groupIds: string[]) => void;
  placeOnHold: () => void;
  addFunds: () => void;
  editManualOrder: () => void;
  openMarkAsFulfilledModal: (groups: readonly FulfillmentGroupData[]) => void;
  mixedPackingSlipSizes: boolean;
  mixedPersonalizedSlipSizes: boolean;
  submitCarrierClaim: () => void;
  startReturn: (orderId: string) => void;
  openCancelOrderModal: (
    groups: readonly FulfillmentGroupData[],
    onOrdersCancelled?: (groupIds: string[]) => void,
  ) => void;
  openPackingSlipBinModal?: (
    productsWithoutBins: FulfillmentOrderLineItem[],
    onProceedWithoutBin: () => void,
    onSaveAndContinue: () => void,
    onExit: () => void,
  ) => void;
  createReturnLabel: (fulfillmentGroup: FulfillmentGroupData) => void;
  openOverrideNotReceivedModal: (revert: boolean) => void;
  openUnmergeModal?: () => void;
  openMissingItemsModal: () => void;
  openFreightBookingModal: () => void;
  createPurchaseOrder: (fulfillmentGroupId: string) => void;
  viewPurchaseOrder: (purchaseOrderId: string) => void;
  navigateToScanAndVerify: (fulfillmentGroupId: string) => void;
  deselectNonPurchasableGroups?: (groupIds: string[]) => void;
  openBinSelectionModal?: (params: OpenBinSelectionModalParams) => void;
  openInventoryAdjustmentWarningModal?: (
    params: OpenInventoryAdjustmentWarningModalParams,
  ) => void;
  openInsufficientInventoryModal: (
    params: OpenInsufficientInventoryModalParams,
  ) => void;
  executeWithPinCheck?: (
    action: () => void,
    config: PinVerificationConfig,
    modalOptions?: { title?: string; description?: string },
  ) => void;
  deviceLocationId: LocationId | undefined;
}

type LabelPurchaseAction =
  | FulfillmentGroupActions.CREATE_LABEL
  | FulfillmentGroupActions.CREATE_AND_PRINT_LABEL;

export type PurchaseFulfillmentLabelsParams = Pick<
  DoOMSActionParams,
  | "user"
  | "client"
  | "fulfillmentGroups"
  | "purchasableGroups"
  | "team"
  | "reloadBalance"
  | "printBatch"
  | "bulkOneClickPrintDocuments"
  | "onGroupsFulfillment"
  | "mixedPackingSlipSizes"
  | "mixedPersonalizedSlipSizes"
  | "deselectNonPurchasableGroups"
  | "openBinSelectionModal"
  | "openInventoryAdjustmentWarningModal"
  | "openInsufficientInventoryModal"
  | "executeWithPinCheck"
  | "deviceLocationId"
> & { action: LabelPurchaseAction };

export type PrintFulfillmentLabelsParams = Pick<
  DoOMSActionParams,
  | "fulfillmentGroups"
  | "team"
  | "printBatch"
  | "mixedPackingSlipSizes"
  | "mixedPersonalizedSlipSizes"
>;

function getFulfillmentPrintBatchMetadata({
  fulfillmentGroups,
  team,
}: Pick<DoOMSActionParams, "fulfillmentGroups" | "team">) {
  const packingSlipIds = fulfillmentGroups
    .map(
      (group) =>
        group.packingSlipTemplateId ??
        team?.settings?.outboundLabels?.packingSlipId,
    )
    .filter((id): id is string => id !== undefined);
  const personalizedSlipIds = personalizedSlipsAccessible(
    team?.settings?.outboundLabels,
  )
    ? fulfillmentGroups
        .map((group) => group.personalizedSlipTemplateId)
        .filter((id): id is string => id !== undefined)
    : [];

  return {
    documentPrintStatusMap:
      getDocumentPrintStatusMapForFulfillmentGroups(fulfillmentGroups),
    packingSlipIds,
    personalizedSlipIds,
  };
}

export function printFulfillmentLabels({
  fulfillmentGroups,
  team,
  printBatch,
  mixedPackingSlipSizes,
  mixedPersonalizedSlipSizes,
}: PrintFulfillmentLabelsParams): void {
  const { documentPrintStatusMap, packingSlipIds, personalizedSlipIds } =
    getFulfillmentPrintBatchMetadata({ fulfillmentGroups, team });

  printBatch({
    batch: { fulfillmentGroupIds: fulfillmentGroups.map((group) => group._id) },
    document: PrintableDocument.LABEL,
    documentPrintStatusMap,
    somePrinted: fulfillmentGroups.some(
      (group) => group.printStatus?.[PrintKind.LABEL],
    ),
    mixedPackingSlipSizes,
    packingSlipIds,
    personalizedSlipIds,
  });
}

export async function purchaseFulfillmentLabels(
  {
    action,
    user,
    client,
    fulfillmentGroups,
    purchasableGroups,
    team,
    reloadBalance,
    printBatch,
    bulkOneClickPrintDocuments,
    onGroupsFulfillment,
    mixedPackingSlipSizes,
    mixedPersonalizedSlipSizes,
    deselectNonPurchasableGroups,
    openBinSelectionModal,
    openInventoryAdjustmentWarningModal,
    openInsufficientInventoryModal,
    executeWithPinCheck,
    deviceLocationId,
  }: PurchaseFulfillmentLabelsParams,
  signal?: AbortSignal,
): Promise<void> {
  if (!user) {
    throw new Error("User not found - cannot buy labels");
  }

  if (purchasableGroups.length === 0) {
    throw new Error("No fulfillments are ready for label purchase");
  }

  const { documentPrintStatusMap, packingSlipIds, personalizedSlipIds } =
    getFulfillmentPrintBatchMetadata({ fulfillmentGroups, team });
  const purchasableGroupIds = new Set(
    purchasableGroups.map((group) => group._id),
  );
  const nonPurchasableGroups = fulfillmentGroups.filter(
    (group) => !purchasableGroupIds.has(group._id),
  );

  if (nonPurchasableGroups.length > 0) {
    deselectNonPurchasableGroups?.(
      nonPurchasableGroups.map((group) => group._id),
    );
    showNotification(
      getSkippedShipmentsNotificationInstance(
        purchasableGroups,
        nonPurchasableGroups,
      ),
    );
  }

  await purchaseLabelsWithInventoryChecks(
    {
      client,
      deviceLocationId,
      executeWithPinCheck,
      fulfillmentGroupIds: purchasableGroups.map((group) => group._id),
      onPurchase: async (ids) => {
        const idsToPurchase = new Set(ids);
        const groupsToPurchase = purchasableGroups.filter((group) =>
          idsToPurchase.has(group._id),
        );
        const createAndPrintDocuments =
          action === FulfillmentGroupActions.CREATE_AND_PRINT_LABEL &&
          bulkOneClickPrintDocuments
            ? getBulkOneClickPrintDocuments(bulkOneClickPrintDocuments)
            : [PrintableDocument.LABEL];
        const bulkOneClickPrintPickListPosition =
          getBulkOneClickPrintPickListPosition(createAndPrintDocuments);
        const shouldPrintLabels =
          action === FulfillmentGroupActions.CREATE_AND_PRINT_LABEL &&
          createAndPrintDocuments.includes(PrintableDocument.LABEL);
        const buyRequest = {
          buyRequests: groupsToPurchase.map((fulfillmentGroup) => ({
            fulfillmentGroupId: fulfillmentGroup._id,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            shipmentRate: fulfillmentGroup.selectedShippingRate!,
            lineItems: fulfillmentGroup.items.map((item) => ({
              id: item.externalId,
              quantity: item.unfulfilledQuantity,
            })),
          })),
          userId: user._id,
          print: shouldPrintLabels,
          initiationTime: Date.now(),
        };

        const response = await client.buyLabels(buyRequest, { signal });
        onGroupsFulfillment(groupsToPurchase.map((group) => group._id));
        if (action === FulfillmentGroupActions.CREATE_AND_PRINT_LABEL) {
          const batch = {
            batchId: response.batchId,
            batchNumber: response.batchNumber,
          };
          if (!bulkOneClickPrintDocuments) {
            printBatch({
              batch,
              document: PrintableDocument.LABEL,
              documentPrintStatusMap: createDocumentPrintStatusMap({
                [PrintableDocument.LABEL]: true,
              }),
              mixedPackingSlipSizes,
              packingSlipIds,
              personalizedSlipIds,
            });
            return;
          }
          if (bulkOneClickPrintPickListPosition) {
            printBatch(
              getBulkOneClickPrintBatchInput({
                batch,
                document: PrintableDocument.LABEL,
                documentPrintStatusMap,
                mixedPackingSlipSizes,
                packingSlipIds,
                personalizedSlipIds,
                bulkOneClickPrintPickListPosition,
              }),
            );
            return;
          }
          for (const document of createAndPrintDocuments) {
            printBatch(
              getBulkOneClickPrintBatchInput({
                batch,
                document,
                documentPrintStatusMap,
                mixedPackingSlipSizes,
                packingSlipIds,
                personalizedSlipIds,
              }),
            );
          }
        }
      },
      onPurchaseSettled: reloadBalance,
      openBinSelectionModal,
      openInsufficientInventoryModal,
      openInventoryAdjustmentWarningModal,
      outboundLabelSettings: team?.settings?.outboundLabels,
      user,
    },
    signal,
  );
}

export const doOMSAction = async (
  {
    action,
    user,
    client,
    fulfillmentGroups,
    purchasableGroups,
    team,
    resetSelections,
    reloadBalance,
    placeOnHold,
    addFunds,
    triggerRefresh,
    printBatch,
    bulkOneClickPrintDocuments,
    onGroupsFulfillment,
    openMarkAsFulfilledModal,
    editManualOrder,
    mixedPackingSlipSizes,
    mixedPersonalizedSlipSizes,
    submitCarrierClaim,
    startReturn,
    openPackingSlipBinModal,
    openCancelOrderModal,
    createReturnLabel,
    openOverrideNotReceivedModal,
    openUnmergeModal,
    openMissingItemsModal,
    openFreightBookingModal,
    createPurchaseOrder,
    viewPurchaseOrder,
    navigateToScanAndVerify,
    deselectNonPurchasableGroups,
    openBinSelectionModal,
    openInventoryAdjustmentWarningModal,
    openInsufficientInventoryModal,
    executeWithPinCheck,
    deviceLocationId,
  }: DoOMSActionParams,
  signal?: AbortSignal,
): Promise<void> => {
  const { documentPrintStatusMap, packingSlipIds, personalizedSlipIds } =
    getFulfillmentPrintBatchMetadata({ fulfillmentGroups, team });
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

  switch (action) {
    case FulfillmentGroupActions.CREATE_LABEL:
    case FulfillmentGroupActions.CREATE_AND_PRINT_LABEL: {
      await purchaseFulfillmentLabels(
        {
          action,
          user,
          client,
          fulfillmentGroups,
          purchasableGroups,
          team,
          reloadBalance,
          printBatch,
          bulkOneClickPrintDocuments,
          onGroupsFulfillment,
          mixedPackingSlipSizes,
          mixedPersonalizedSlipSizes,
          deselectNonPurchasableGroups,
          openBinSelectionModal,
          openInventoryAdjustmentWarningModal,
          openInsufficientInventoryModal,
          executeWithPinCheck,
          deviceLocationId,
        },
        signal,
      );
      break;
    }
    case FulfillmentGroupActions.READY_FOR_PICKUP: {
      if (!user) {
        throw new Error("User not found - cannot ready for pickup");
      }

      let toastMsg = "";
      let toastVariant: "success" | "error" | "warning" = "success";
      try {
        const response = await client.markAsReadyForPickup({
          fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
          userId: user._id,
        });
        toastMsg = `Batch #${response.batchNumber} marked as ready for pickup`;

        // If local pickup label printing is enabled, print the label
        if (
          team?.settings?.outboundLabels?.documents?.localPickupLabel?.enabled
        ) {
          printBatch({
            batch: {
              batchId: response.batchId,
              batchNumber: response.batchNumber,
            },
            document: PrintableDocument.LOCAL_PICK_UP_LABEL,
            documentPrintStatusMap: createDocumentPrintStatusMap({
              [PrintableDocument.LOCAL_PICK_UP_LABEL]: true,
            }),
          });
        }
      } catch (e) {
        toastMsg = "Error marking as ready for pickup";
        toastVariant = "error";
      } finally {
        onGroupsFulfillment(fulfillmentGroups.map((g) => g._id));
        toast(toastMsg, { variant: toastVariant });
      }
      break;
    }
    case FulfillmentGroupActions.MARK_AS_PICKED_UP: {
      let toastMsg = "";
      let toastVariant: "success" | "error" | "warning" = "success";
      try {
        await client.markAsPickedUp({
          fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
        });
        toastMsg = "Orders marked as picked up";
      } catch (e) {
        toastMsg = "Error marking as picked up";
        toastVariant = "error";
      } finally {
        onGroupsFulfillment(fulfillmentGroups.map((g) => g._id));
        toast(toastMsg, { variant: toastVariant });
      }
      break;
    }
    case FulfillmentGroupActions.MARK_AS_FULFILLED: {
      const doMarkAsFulfilled = () =>
        openMarkAsFulfilledModal(fulfillmentGroups);
      if (executeWithPinCheck) {
        executeWithPinCheck(doMarkAsFulfilled, {
          type: PinVerificationType.MarkFulfilled,
        });
      } else {
        doMarkAsFulfilled();
      }
      break;
    }
    case FulfillmentGroupActions.PRINT_LABEL: {
      printFulfillmentLabels({
        fulfillmentGroups,
        team,
        printBatch,
        mixedPackingSlipSizes,
        mixedPersonalizedSlipSizes,
      });
      break;
    }
    case FulfillmentGroupActions.PRINT_PACKING_SLIP: {
      console.error("Clicked print packing slip for groups", {
        fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
        team: team?._id,
      });

      const doPrintPackingSlip = () => {
        printBatch({
          batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
          document: PrintableDocument.PACKING_SLIP,
          documentPrintStatusMap,
          somePrinted: fulfillmentGroups.some(
            (g) => g.printStatus?.[PrintKind.PACKING_SLIP],
          ),
          packingSlipIds,
        });
      };

      // Check if we should show the bin assignment modal
      if (
        team?.settings?.outboundLabels?.canUpdateBinsFromTable &&
        openPackingSlipBinModal
      ) {
        try {
          const productsWithoutBins = await getProductsWithoutBins(
            client,
            fulfillmentGroups,
          );

          if (productsWithoutBins.length > 0) {
            // Show the packing slip bin modal
            openPackingSlipBinModal(
              productsWithoutBins,
              doPrintPackingSlip, // onProceedWithoutBin
              doPrintPackingSlip, // onSaveAndContinue
              () => {}, // onExit - do nothing
            );
            break;
          }
        } catch (error) {
          console.error("Error checking for products without bins:", error);
          // If there's an error checking bins, just proceed with printing
        }
      }

      // If no bin check needed or no products without bins, proceed with printing
      doPrintPackingSlip();
      break;
    }
    case FulfillmentGroupActions.PRINT_PERSONALIZED_SLIP: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.PERSONALIZED_SLIP,
        documentPrintStatusMap,
        skipPrinter: mixedPersonalizedSlipSizes,
        personalizedSlipIds,
      });

      break;
    }
    case FulfillmentGroupActions.PRINT_PICK_LIST: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.PICK_LIST,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.PRINT_ORDER_SUMMARY: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.ORDER_SUMMARY,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.COMMERCIAL_INVOICE: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.COMMERCIAL_INVOICE,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.PLACE_HOLD: {
      placeOnHold();
      break;
    }
    case FulfillmentGroupActions.RELEASE_HOLD: {
      let toastMsg = "";
      let toastVariant: "success" | "error" | "warning" = "success";
      const { failedGroupIds } = await client.releaseAllHolds({
        fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
      });
      if (failedGroupIds.length > 0) {
        toastMsg = `Failed to release holds from ${failedGroupIds.length} fulfillments`;
        toastVariant = "error";
      } else {
        toastVariant = "success";
      }
      toast(toastMsg, { variant: toastVariant });
      break;
    }
    case FulfillmentGroupActions.UNMERGE: {
      if (fulfillmentGroups.length === 1 && openUnmergeModal) {
        openUnmergeModal();
      } else {
        let toastMsg = "Successfully unmerged fulfillments";
        let toastVariant: "success" | "error" | "warning" = "success";
        try {
          await client.unmergeFulfillmentGroups({
            fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
          });
          toast(toastMsg, { variant: toastVariant });
        } catch (error) {
          toastMsg = "Error unmerging fulfillments";
          toastVariant = "error";
          toast(toastMsg, { variant: toastVariant });
        }
        triggerRefresh();
        resetSelections();
      }
      break;
    }
    case FulfillmentGroupActions.ADD_FUNDS_AND_CREATE: {
      addFunds();
      break;
    }
    case FulfillmentGroupActions.EDIT_ORDER: {
      editManualOrder();
      break;
    }
    case FulfillmentGroupActions.NOTIFY_MARKETPLACE: {
      if (fulfillmentGroups.length !== 1) {
        toast("Can only notify marketplace for one fulfillment at a time", {
          variant: "error",
        });
        break;
      }

      let toastMsg = "";
      let toastVariant: "success" | "error" | "warning" = "success";
      try {
        await client.notifyMarketplace({
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          fulfillmentGroupId: fulfillmentGroups[0]._id,
        });
        toastMsg = "Marketplace notification sent";
      } catch (e) {
        toastMsg = "Error sending marketplace notification";
        toastVariant = "error";
        console.error(
          "Error sending marketplace notification",
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          (e as Error).message,
        );
      } finally {
        toast(toastMsg, { variant: toastVariant });
      }
      break;
    }
    case FulfillmentGroupActions.SUBMIT_CARRIER_CLAIM: {
      const canSubmitCarrierClaim =
        getCarrierClaimActionEligibility(
          fulfillmentGroups,
        ).canSubmitCarrierClaim;

      if (!canSubmitCarrierClaim) {
        toast("Can only submit carrier claim for one shipment at a time", {
          variant: "error",
        });
        break;
      }

      submitCarrierClaim();
      break;
    }
    case FulfillmentGroupActions.EXPORT_PICK_LIST_CSV: {
      const response = await client.getPickListCsv({
        fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
      });
      const url = response.url;
      const link = document.createElement("a");
      link.href = url;
      link.download = "pick_list.csv";
      document.body.append(link);
      link.click();
      link.remove();
      break;
    }
    case FulfillmentGroupActions.CREATE_RETURN_LABEL: {
      if (fulfillmentGroups.length !== 1) {
        toast("Can only create return label for one shipment at a time", {
          variant: "error",
        });
        break;
      }

      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      createReturnLabel(fulfillmentGroups[0]);
      break;
    }
    case FulfillmentGroupActions.PRINT_RETURN_LABEL: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.RETURN_LABEL,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.PRINT_LOCAL_PICKUP_LABEL: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.LOCAL_PICK_UP_LABEL,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.OVERRIDE_NOT_RECEIVED: {
      openOverrideNotReceivedModal(false);
      break;
    }
    case FulfillmentGroupActions.REVERT_RECEIVED_OVERRIDE: {
      openOverrideNotReceivedModal(true);
      break;
    }
    case FulfillmentGroupActions.RESOLVE_PROBLEM_ORDER: {
      if (fulfillmentGroups.length !== 1) {
        toast("Can only resolve problem order at a time", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 3 }),
        });
      } else {
        openMissingItemsModal();
      }
      break;
    }
    case FulfillmentGroupActions.BOOK_FREIGHT: {
      openFreightBookingModal();
      break;
    }
    case FulfillmentGroupActions.PRINT_BOL: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.BILL_OF_LADING,
        documentPrintStatusMap,
      });
      break;
    }
    case FulfillmentGroupActions.CREATE_PURCHASE_ORDER: {
      if (fulfillmentGroups.length === 1 && fulfillmentGroups[0]) {
        const groupId = fulfillmentGroups[0]._id;
        const doCreatePurchaseOrder = () => createPurchaseOrder(groupId);
        if (executeWithPinCheck) {
          executeWithPinCheck(doCreatePurchaseOrder, {
            type: PinVerificationType.CreatePurchaseOrder,
          });
        } else {
          doCreatePurchaseOrder();
        }
      }
      break;
    }
    case FulfillmentGroupActions.VIEW_PURCHASE_ORDER: {
      if (fulfillmentGroups.length === 1 && fulfillmentGroups[0]) {
        const purchaseOrderId = fulfillmentGroups[0].linkedPurchaseOrderId;
        if (purchaseOrderId) {
          viewPurchaseOrder(purchaseOrderId);
        }
      }
      break;
    }
    case FulfillmentGroupActions.SCAN_AND_VERIFY: {
      if (fulfillmentGroups.length === 1 && fulfillmentGroups[0]) {
        navigateToScanAndVerify(fulfillmentGroups[0]._id);
      }
      break;
    }
    case FulfillmentGroupActions.PRINT_PARCEL_PACKING_SLIPS: {
      printBatch({
        batch: { fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id) },
        document: PrintableDocument.PARCEL_PACKING_SLIP,
        documentPrintStatusMap,
      });
      break;
    }
    default:
      if (action) {
        assertNever(action);
      }
  }
};
